/**
 * 屏幕 2：四宫格画廊
 * 4 个怪物锁定 4 套互不相同的 (bodyType, palette, variant) 组合
 * → 视觉上一定互不相同
 * → 状态机记录 combo，screen3 复用同一只怪
 */

import { generateMonster, animateIdle, BODY_TYPES, type MonsterFrames, type BodyType } from "../spriteLib";
import { createSnow } from "../snow";
import type { CharAnalysis, MonsterDesc } from "../../shared/types";

export interface Screen2Combo {
  bodyType: BodyType;
  paletteName: string;
  variant: 0 | 1 | 2;
}

export interface Screen2Deps {
  root: HTMLElement;
  fontFamily: string;
  analysis: CharAnalysis;
  monsters: MonsterDesc[];
  combos: Screen2Combo[];
  onSelect: (index: number) => void;
  onRegenerate: () => void;
  onBack: () => void;
  onGoZoo?: () => void;
}

export interface Screen2Controller {
  destroy: () => void;
}

// 4 宫格锁定：保留作 fallback。实际使用 pickRandomCombos() 随机抽
const LOCKED_COMBOS: Screen2Combo[] = [
  { bodyType: "humanoid", paletteName: "PinkPop", variant: 0 },
  { bodyType: "beast", paletteName: "AcidGreen", variant: 1 },
  { bodyType: "abstract", paletteName: "SunYellow", variant: 2 },
  { bodyType: "winged", paletteName: "Ultraviolet", variant: 0 },
];

function shuffleInPlace<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

/**
 * 从 12 个体型里随机抽 n 个不同的
 * → 同名多次输入会得到不同的 4 只组合
 * → 不再"永远是同一组 4 只"
 */
export function pickRandomCombos(n: number = 4): Screen2Combo[] {
  const shuffled = [...BODY_TYPES];
  shuffleInPlace(shuffled);
  const chosen = shuffled.slice(0, n);
  // 12 套调色板随机选 n 套
  const palettePool = [
    "PinkPop", "AcidGreen", "SunYellow", "Ultraviolet",
    "ElectricBlue", "Magma", "CyanPulse", "LimeShock",
    "VioletHaze", "Tangerine", "MintDream", "HotRose",
  ];
  const palettes = [...palettePool];
  shuffleInPlace(palettes);
  return chosen.map((bodyType, i) => ({
    bodyType,
    paletteName: palettes[i % palettes.length],
    variant: Math.floor(Math.random() * 3) as 0 | 1 | 2,
  }));
}

// 备选池（如果传入的 combos 数量 > 4，则从池中选不同的）
const FALLBACK_COMBOS: Screen2Combo[] = [
  { bodyType: "blob", paletteName: "Tangerine", variant: 1 },
  { bodyType: "tall", paletteName: "Magma", variant: 2 },
  { bodyType: "stout", paletteName: "MintDream", variant: 0 },
  { bodyType: "multi_leg", paletteName: "CyanPulse", variant: 1 },
  { bodyType: "humanoid", paletteName: "HotRose", variant: 2 },
  { bodyType: "beast", paletteName: "VioletHaze", variant: 0 },
  { bodyType: "abstract", paletteName: "LimeShock", variant: 1 },
  { bodyType: "winged", paletteName: "ElectricBlue", variant: 2 },
];

export function createScreen2(deps: Screen2Deps): Screen2Controller {
  const { root, analysis, monsters, combos, onSelect, onRegenerate, onBack, onGoZoo } = deps;

  const destroySnow = createSnow(root);

  const screen = document.createElement("div");
  screen.className = "screen-2";

  const title = document.createElement("div");
  title.className = "title";
  title.textContent = `「${analysis.raw}」异化结果 × 4`;
  screen.appendChild(title);

  const grid = document.createElement("div");
  grid.className = "grid";
  screen.appendChild(grid);

  const stops: Array<() => void> = [];

  monsters.forEach((m, i) => {
    const combo = combos[i] ?? LOCKED_COMBOS[i] ?? LOCKED_COMBOS[0];

    const cell = document.createElement("div");
    cell.className = "cell";

    const canvas = document.createElement("canvas");
    canvas.width = 128;
    canvas.height = 128;
    const frames: MonsterFrames = generateMonster(
      m,
      undefined,
      combo.bodyType,
      combo.paletteName,
      combo.variant
    );
    const stop = animateIdle(canvas, frames, 2);
    stops.push(stop);
    cell.appendChild(canvas);

    const label = document.createElement("div");
    label.className = "label";
    label.textContent = m.class || `异化体 ${i + 1}`;
    cell.appendChild(label);

    const scpid = document.createElement("div");
    scpid.className = "scpid";
    scpid.textContent = m.scpId;
    cell.appendChild(scpid);

    const typeTag = document.createElement("div");
    typeTag.className = "scpid";
    typeTag.textContent = `[${combo.bodyType} · ${combo.paletteName}]`;
    cell.appendChild(typeTag);

    cell.addEventListener("click", () => onSelect(i));
    grid.appendChild(cell);
  });

  const actions = document.createElement("div");
  actions.className = "actions";
  actions.innerHTML = `
    <button class="primary">再生 4 个</button>
    <button class="secondary">换名字</button>
    <button class="nav-btn">捕获图鉴</button>
  `;
  const regenBtn = actions.querySelector(".primary") as HTMLButtonElement;
  const backBtn = actions.querySelector(".secondary") as HTMLButtonElement;
  const zooBtn = actions.querySelector(".nav-btn") as HTMLButtonElement;
  regenBtn.addEventListener("click", () => onRegenerate());
  backBtn.addEventListener("click", () => onBack());
  if (onGoZoo) zooBtn.addEventListener("click", () => onGoZoo());
  else zooBtn.style.display = "none";
  screen.appendChild(actions);

  root.appendChild(screen);

  const destroy = () => {
    stops.forEach((s) => s());
    destroySnow();
    screen.remove();
  };

  return { destroy };
}

export function getDefaultCombos(n: number = 4): Screen2Combo[] {
  const out: Screen2Combo[] = [...LOCKED_COMBOS];
  if (n > out.length) {
    out.push(...FALLBACK_COMBOS.slice(0, n - out.length));
  }
  return out;
}
